import { useEffect, useState } from "react";
import { api, API, getToken } from "@/lib/api";
import { Share2, Copy, Check, Trash2 } from "lucide-react";
import { t, withLang } from "@/lib/i18n";

const ShareRecord = () => {
  const [token, setToken] = useState(null);
  const [expiresIn, setExpiresIn] = useState(""); // "" | "7" | "30" | "90"
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // On first render, we don't fetch — creating is idempotent (returns existing)
  }, []);

  const url = token ? withLang(`${window.location.origin}/r/${token}`) : "";

  const generate = async () => {
    setLoading(true);
    try {
      const url = expiresIn ? `/share?expires_in_days=${expiresIn}` : "/share";
      const { data } = await api.post(url);
      setToken(data.token);
    } finally {
      setLoading(false);
    }
  };

  const revoke = async () => {
    if (!confirm("Revoke this share link? Anyone with the old link will lose access.")) return;
    await api.delete("/share");
    setToken(null);
    setCopied(false);
  };

  const copy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadPdf = async () => {
    // Fetch the PDF with auth header via api instance, then trigger download
    const res = await fetch(`${API}/report/pdf`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    const blob = await res.blob();
    const dlUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = dlUrl;
    a.download = "radon_record.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(dlUrl);
  };

  return (
    <div className="card-flat" data-testid="share-record">
      <div className="flex items-center gap-2 mb-2">
        <Share2 size={16} className="text-[#2F524A]" />
        <h3 className="font-serif text-xl">{t("Share & export")}</h3>
      </div>
      <p className="text-sm text-[#5C6661]">
        {t("Send a read-only link to a spouse, landlord, or contractor — or download your Radon Record as a PDF.")}
      </p>

      <div className="mt-4 flex flex-wrap gap-2 items-center">
        {!token && (
          <>
            <select
              value={expiresIn}
              onChange={(e) => setExpiresIn(e.target.value)}
              className="text-sm border border-[#E8ECEA] rounded-xl px-3 py-2 bg-white text-[#1C201F]"
              data-testid="share-expiry-select"
            >
              <option value="">{t("No expiry")}</option>
              <option value="7">{t("Expires in 7 days")}</option>
              <option value="30">{t("Expires in 30 days")}</option>
              <option value="90">{t("Expires in 90 days")}</option>
            </select>
            <button onClick={generate} disabled={loading}
              className="btn-primary !py-2 !px-4 text-sm inline-flex items-center gap-2"
              data-testid="share-generate-button">
              <Share2 size={14} /> {loading ? t("Generating…") : t("Generate share link")}
            </button>
          </>
        )}
        <button onClick={downloadPdf}
          className="btn-secondary !py-2 !px-4 text-sm inline-flex items-center gap-2"
          data-testid="pdf-download-button">
          {t("Download PDF report")}
        </button>
      </div>

      {token && (
        <div className="mt-4 rounded-xl border border-[#E8ECEA] bg-[#F4F7F6] p-3 flex items-center gap-3" data-testid="share-link-panel">
          <input readOnly className="flex-1 bg-transparent text-sm text-[#1C201F] focus:outline-none"
            value={url} data-testid="share-link-input" />
          <button onClick={copy} className="text-xs font-semibold text-[#2F524A] hover:underline inline-flex items-center gap-1" data-testid="share-copy-button">
            {copied ? <><Check size={12} />{" "}{t("Copied")}</> : <><Copy size={12} />{" "}{t("Copy")}</>}
          </button>
          <button onClick={revoke} className="text-[#5C6661] hover:text-[#9B2C2C] p-1" aria-label={t("Revoke")} data-testid="share-revoke-button">
            <Trash2 size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ShareRecord;
